import React, { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';

import { FormControl, IconButton } from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import DeleteIcon from '@mui/icons-material/Delete';
import _ from 'lodash';
import { toast } from 'react-toastify';
import heic2any from 'heic2any';

import { IMAGE_TYPES } from 'constants/images';
import { ImagesThumb } from 'containers/ManageDelivery/components';
import { convertImage } from 'utils/image';
import { Section } from './styled';

const PREFIX = 'DropoffPhoto';

const classes = {
  root: `${PREFIX}-root`,
  text: `${PREFIX}-text`,
  add: `${PREFIX}-add`,
  image: `${PREFIX}-image`,
};

const StyledSection = styled(Section)(({ theme }) => ({
  [`& .${classes.root}`]: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    border: `1px dashed ${theme.colorTokens.outlineVariant}`,
    cursor: 'pointer',
  },

  [`& .${classes.text}`]: {
    color: '#b7b6b7',
    fontSize: '14px',
    textAlign: 'center',
  },

  [`& .${classes.add}`]: {
    fontSize: '14px',
    color: '#4a90e2',
    textAlign: 'center',
    padding: '0.5rem 0',
    backgroundColor: 'transparent',
    border: 'none',
    textDecoration: 'underline',
    textUnderlineOffset: '0.2rem',
    cursor: 'pointer',
  },

  [`& .${classes.image}`]: {
    width: '100%',
    objectFit: 'cover',
    marginBottom: '0.5rem',
  },
}));

export const DropoffPhoto = React.forwardRef((props, ref) => {
  const [images, setImages] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const { onChange, value, ...rest } = props;

  const handleChange = async (e) => {
    const image = e.target.files?.[0];
    if (!image) return;

    const type = image.type;
    if (!IMAGE_TYPES.includes(type)) {
      toast.error('Image type is not supported');
      return;
    }

    setIsConverting(true);
    if (!['image/heic', 'image/heif'].includes(type)) {
      const result = await convertImage(image, 'jpeg');
      const data = [...images, { url: result }];
      onChange(data);
      setImages(data);
      setIsConverting(false);
      return;
    }

    let blobURL = URL.createObjectURL(image);
    let blobRes = await fetch(blobURL);
    let blob = await blobRes.blob();
    let result = await heic2any({
      blob,
      toType: 'image/jpeg',
      quality: 0.8,
    });
    const data = [...images, { url: result }];
    onChange(data);
    setImages(data);
    setIsConverting(false);
  };

  const handleDeleteImage = () => {
    setImages([]);
    onChange([]);
  };

  const handleDeleteImageByIndex = (idx) => {
    const data = images.filter((_, index) => idx !== index);
    onChange(data);
    setImages(data);
  };

  useEffect(() => {
    if (value.length > 0 && _.some(value, (img) => typeof img === 'string')) {
      setImages(value.map((s) => ({ url: s })));
    }
  }, [value]);

  return (
    <StyledSection>
      <div className="section__header">
        <h4 className="section__title section__title--no-margin section__title--h40">Preferred Drop-off:</h4>
        {images.length > 0 && (
          <IconButton onClick={handleDeleteImage} size="large">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <div className="section__body">
        <FormControl fullWidth>
          <input
            type="file"
            ref={ref}
            {...rest}
            hidden
            id="attachment"
            accept={IMAGE_TYPES.join(', ')}
            onChange={handleChange}
            disabled={isConverting ? 'disabled' : ''}
          />
          {images.length ? (
            <>
              <div className="image">
                <ImagesThumb pods={images} title="Preferred Drop-off" onDelete={handleDeleteImageByIndex} />
              </div>
              <label htmlFor="attachment" className={classes.add}>
                + Add another photo
              </label>
            </>
          ) : (
            <div>
              <label htmlFor="attachment" className={classes.root}>
                <AddAPhotoIcon />
                <span className={classes.text}>Please share a photo of where our driver should leave your package...</span>
              </label>
            </div>
          )}
        </FormControl>
      </div>
    </StyledSection>
  );
});
